import React, { useRef, useState } from "react";
import { DateTimeStandard, Icons, Box, Chip, Stack } from "@curves/ui-base";
import { isEmpty } from "lodash";
import Tablenization from "@icoach/components/Tablenization";
import { MemberSecureCard, MemoControllerBox, MoreLabelBox, MorePageBox } from "@icoach/components/";
import { formatCurrencyFn, isFunction, mergeQueryToUrl } from "@util/utils";
import { displayDateText, parseDate } from "@util/moment";
import { getCabinetMoreActionArray, CABINET_STATUS_COLOR_MATCH } from "@icoach/rentCabinet/staticData";
import { CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import { useRentCabinetContext } from "@icoach/rentCabinet/components/RentCabinetContext";
import { ListInfo } from "@icoach/accountBook/components";
import ScrollerArrowButton from "@icoach/inbody/components/ScrollerArrowButton";
import DismissibleTooltip from "@icoach/components/DismissibleTooltip";
import InteractiveStatusChip from "@icoach/components/InteractiveStatusChip";
import usePrivacyToggle, { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import { MEMBERS_MEMBER_DETAIL_TABS } from "@icoach/constants";

export const StatusChipTooltip = ({ icon: Icon = <Icons.QuestionCircle fontSize="small" htmlColor="#ef5350" />, statusText, statusMemo, color, isShowTip = false }) => {
	const [open, setOpen] = useState(false);

	const handleTooltipClose = () => {
		setOpen(false);
	};

	const handleTooltipToggle = () => {
		setOpen(prev => !prev);
	};

	return isShowTip ? (
		<DismissibleTooltip open={open} onToggle={handleTooltipToggle} onClose={handleTooltipClose} tip={statusMemo}>
			<Chip label={statusText} icon={Icon} color={color} />
		</DismissibleTooltip>
	) : (
		<Chip label={statusText} color={color} />
	);
};

const MoreLabel = ({ rowsData, refresh, dialogRef, ...props }) => {
	const { cabinetID } = rowsData;
	const moreLabelMap = {
		edit: {
			text: "編輯置物櫃",
			onClickEvent: () => dialogRef.current && dialogRef.current.openEditCabinetDialog({ cabinetID }, refresh),
		},
		addRent: {
			text: "新增租約",
			to: () => mergeQueryToUrl(CLUB_MEMBERS_ROUTER.CABINET.GET_CONTRACT_RENTCABINETID_PATH("contract", 0), { cabinetID }),
		},
		delete: {
			text: "刪除置物櫃",
			onClickEvent: () => dialogRef.current && dialogRef.current.openDeleteCabinetDialog({ cabinetID }, refresh),
		},
	};

	return <MoreLabelBox {...props} moreLabelMap={moreLabelMap} textAlign="center" />;
};

const rowsLabel = [
	{
		cellKey: "cabinetName",
		headerLabel: "置物櫃名稱",
		align: "center",
	},
	{
		cellKey: "cabinetFee",
		headerLabel: "月租定價",
		align: "right",
		formatCell: cellValue => {
			return formatCurrencyFn(cellValue, { symbol: "$" });
		},
	},
	{
		cellKey: "cabinetStatusText",
		headerLabel: "狀態",
		align: "center",
		formatCell: (cellValue, { cabinetStatus, statusMemo }) => {
			return <InteractiveStatusChip label={cellValue} tip={statusMemo} color={CABINET_STATUS_COLOR_MATCH.get(cabinetStatus)} />;
		},
	},
	{
		cellKey: "memberID",
		headerLabel: "承租會員",
		maxWidth: 20.4,
		formatCell: (memberID, { memberNo, displayName, maskDisplayName, memberImg, nickName }, { isRevealed }) => {
			if (!memberID) return "";
			return (
				<MemberSecureCard
					avatar={memberImg}
					name={displayName}
					maskName={maskDisplayName}
					nickname={nickName}
					memberNo={memberNo}
					link={CLUB_MEMBERS_ROUTER.MEMBER_DETAIL.GET_TABS_PATH(memberID, MEMBERS_MEMBER_DETAIL_TABS.RENT_CABINET)}
					isRevealed={isRevealed}
				/>
			);
		},
	},
	{
		cellKey: "startDate",
		headerLabel: "租借期間",
		formatCell: (startDate, { endDate }) => {
			if (!startDate && !endDate) return "";
			return displayDateText(startDate, endDate);
		},
	},
	{
		cellKey: "memo",
		headerLabel: "備註",
		formatCell: cellValue => {
			return <MemoControllerBox data={cellValue} />;
		},
	},
	{
		cellKey: "employeeName",
		headerLabel: "異動擔當",
		align: "center",
	},
	{
		cellKey: "updatedDate",
		headerLabel: "更新日期",
		formatCell: cellValue => {
			return parseDate(cellValue, DateTimeStandard.DisplayDateBySlash);
		},
	},

	{
		cellKey: "more",
		headerLabel: "",
		align: "right",
		isIcon: true,
		formatCell: (cellValue, rowsData, other) => {
			let moreActionArray = getCabinetMoreActionArray(rowsData);
			if (isEmpty(moreActionArray)) return null;
			return <MoreLabel moreActionArray={moreActionArray} rowsData={rowsData} {...other} />;
		},
	},
];

const CabinetOverviewTable = () => {
	const { tableData, setBookmarkNum, refresh, dialogRef } = useRentCabinetContext();
	const { list = [], totalCount, totalPage, pageIndex, pageSize } = tableData;
	const { isRevealed, toggle, renderPrivacyIcon } = usePrivacyToggle();
	const labelTitle = useRef(rowsLabel);
	const scrollerArrowRef = useRef(null);

	const refreshFn = () => {
		if (isFunction(refresh.current)) refresh.current();
	};

	const handlePageChange = (e, page) => {
		setBookmarkNum(page);
	};

	const handleToggleRevealed = () => {
		const apiParams = {
			filterType: securityMaskFilterTypeMap.ClubMemberRentCabinet,
			totalCount: list?.length,
		};
		toggle(apiParams);
	};

	return (
		<React.Fragment>
			<Stack className="mb-1" alignItems="center" justifyContent="space-between">
				{!isEmpty(list) && <Stack className="pb-1">{renderPrivacyIcon(handleToggleRevealed)}</Stack>}
				<ListInfo className="ml-auto" pageIndex={pageIndex} padeSize={pageSize} totalCount={totalCount} isCurrentPageFirstIndex isCurrentPageLastIndex />
			</Stack>
			<Box className="scroll-arrow-container">
				<Tablenization
					cardProps={{ className: "theme-gray overview" }}
					count={totalPage}
					page={pageIndex}
					onChange={handlePageChange}
					tableContainerRef={scrollerArrowRef}
					Pagination={MorePageBox}
					isPagination
				>
					<Tablenization.TableRowHeader headerRow={labelTitle.current} />
					<Tablenization.TableRowBody headerRow={labelTitle.current} rowsData={list} dialogRef={dialogRef} refresh={refreshFn} isRevealed={isRevealed} />
				</Tablenization>
				<ScrollerArrowButton parentRef={scrollerArrowRef} />
			</Box>
		</React.Fragment>
	);
};

export default CabinetOverviewTable;
